import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_SRC } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";
import { LOCALES, LOCALE_LABELS } from "@/i18n";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t, lp, switchPath, lang } = useLanguage();

  const links = [
    { to: "/a-igniva", label: t("nav.about"), id: "about" },
    { to: "/marcas", label: t("nav.brands"), id: "brands" },
    { to: "/aplicacoes", label: t("nav.applications"), id: "applications" },
    { to: "/explorador", label: t("nav.explorer"), id: "explorer" },
    { to: "/profissionais", label: t("nav.professionals"), id: "professionals" },
    { to: "/contactos", label: t("nav.contacts"), id: "contacts" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        open
          ? "bg-[#121316] border-white/10"
          : scrolled
            ? "bg-[#121316]/97 backdrop-blur-sm border-white/10"
            : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-6">
        <Link to={lp("/")} aria-label="IGNIVA — Home" data-testid="header-logo-link" className="flex items-center shrink-0">
          <img src={LOGO_SRC} alt="IGNIVA — The Art of Surfaces" className="h-9 md:h-10 w-auto" width="312" height="134" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 text-[10px] xl:text-[11px] tracking-[0.14em] xl:tracking-[0.16em] uppercase text-[#D6D3CC]" aria-label="Main">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={lp(l.to)}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) => `nav-link transition-colors hover:text-white whitespace-nowrap ${isActive ? "text-[#C2A675] is-active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <div className="hidden sm:flex items-center gap-2 text-[11px] tracking-[0.14em] text-[#A8A6A0]" data-testid="language-selector">
            {LOCALES.map((l, i) => (
              <span key={l} className="flex items-center gap-2">
                {i > 0 && <span className="text-white/20">/</span>}
                <Link
                  to={switchPath(l)}
                  data-testid={`lang-${l}`}
                  aria-current={lang === l ? "true" : undefined}
                  className={`transition-colors hover:text-white ${lang === l ? "text-[#C2A675]" : ""}`}
                >
                  {LOCALE_LABELS[l]}
                </Link>
              </span>
            ))}
          </div>
          <Link
            to={lp("/orcamento")}
            data-testid="header-cta-button"
            className="hidden md:inline-block px-4 xl:px-5 py-2.5 border border-[#C2A675]/60 text-[#C2A675] text-[11px] uppercase tracking-[0.16em] hover:bg-[#C2A675] hover:text-[#121316] transition-colors duration-300 whitespace-nowrap"
          >
            {t("nav.quote")}
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[#E8E6E1] p-2 -mr-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden fixed inset-0 top-20 bg-[#121316] border-t border-white/10 z-40 overflow-y-auto">
          <nav className="px-6 py-10 flex flex-col gap-1" aria-label="Mobile">
            {[{ to: "/", label: t("nav.home"), id: "home" }, ...links].map((l) => (
              <NavLink
                key={l.to}
                to={lp(l.to)}
                data-testid={`mobile-nav-${l.id}`}
                className={({ isActive }) =>
                  `serif-display text-2xl sm:text-3xl font-light py-3 border-b border-white/5 transition-colors ${
                    isActive ? "text-[#C2A675]" : "text-[#E8E6E1]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to={lp("/orcamento")}
              data-testid="mobile-cta-button"
              className="mt-8 inline-block text-center px-8 py-4 bg-[#C2A675] text-[#121316] text-xs uppercase tracking-[0.2em] font-medium"
            >
              {t("nav.quote")}
            </Link>
            <div className="mt-8 flex items-center gap-3 text-xs tracking-[0.14em] text-[#A8A6A0]">
              {LOCALES.map((l, i) => (
                <span key={l} className="flex items-center gap-3">
                  {i > 0 && <span className="text-white/20">/</span>}
                  <Link to={switchPath(l)} data-testid={`mobile-lang-${l}`} className={lang === l ? "text-[#C2A675]" : ""}>
                    {LOCALE_LABELS[l]}
                  </Link>
                </span>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
